import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { useLanguage } from '@/hooks/useLanguage';
import type { Role } from '@/types/api';
import { LoadingState } from '@/components/ui';

interface ProtectedRouteProps {
  children: ReactNode;
  roles?: Role[];
}

/**
 * Garde de route : exige une session valide et, le cas echeant, un role.
 * Route guard: requires a valid session and, where relevant, a role.
 */
export function ProtectedRoute({ children, roles }: ProtectedRouteProps) {
  const { user, initializing, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();

  if (initializing) return <LoadingState className="min-h-screen" />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Le changement de mot de passe provisoire est bloquant.
  if (user?.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  if (roles && user && !roles.includes(user.role)) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2 text-center">
        <h1 className="font-display text-xl font-bold text-ink">{t('errors.forbidden')}</h1>
        <p className="text-sm text-ink-muted">{t('errors.forbiddenBody')}</p>
      </div>
    );
  }

  return <>{children}</>;
}
